import axios from "axios";
import React, { ChangeEvent, FormEvent, useState, useEffect} from "react";
import { Modal, ModalOverlay, ModalContent, ModalBody } from "@chakra-ui/modal";
import Card from "components/card";
import RoleDropdown from "views/admin/user/components/dropdown/RoleDropdown";
import SubaccountDropdown from "views/admin/user/components/dropdown/SubaccountDropdown";
import SweetAlert2 from "react-sweetalert2";
import { Button } from "@chakra-ui/react";

interface Role {
  role: string;
  merchantId: string;
  subaccountId: string;
}
interface EditRoleModalProps {
  setSwalProps: any;
  isOpen: boolean;
  onClose: () => void;
  selectedRole: any;
  setUserRoles: any;
  roleOptions: string[];
  username: string;
  email: string;
  editUserChannel: BroadcastChannel;
  fetchUserRoles: any;
  userRoles: Role[];
  merchants: { id: string; name: string }[] | null;
  getSubaccountOptions: (
    merchantId: string
  ) => Promise<{ id: string; suba_name: string }[]>;
  isSetSubaccounts: boolean;
  merchantForGetNameList: any[];
}

const EditRoleModal: React.FC<EditRoleModalProps> = ({
  setSwalProps,
  isOpen,
  onClose,
  selectedRole,
  setUserRoles,
  roleOptions,
  username,
  email,
  editUserChannel,
  fetchUserRoles,
  userRoles,
  merchants,
  getSubaccountOptions,
  isSetSubaccounts,
  merchantForGetNameList,
}) => {
  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const sysUsername = localStorage.getItem("username")?.toLowerCase();
  const [editedRole, setEditedRole] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedSubaccount, setSelectedSubaccount] = useState("");
  const [subaccounts, setSubaccounts] = useState<{ id: string; suba_name: string }[]>([]);

  const isOriginalMerchant = selectedRole?.role === "Merchant";

  const currentMerchant = merchantForGetNameList.find((x: any) => x.id === selectedRole?.merchantId);
  const currentMerchantName = currentMerchant ? currentMerchant.name : "";
  const currentSubaccountName = currentMerchant?.subaccount?.find((s: any) => s.id === selectedRole?.subaccountId)?.name || "";

  // Roles that need a subaccount
  const needsSubaccount = (role: string) =>
    role !== "Merchant" && role !== "SuperAdmin" && role !== "Agent";

  const showSubaccountDropdown = isOriginalMerchant && needsSubaccount(editedRole);

  useEffect(() => {
    setEditedRole(selectedRole?.role);
    setSelectedSubaccount("");
    setSubaccounts([]);
  }, [selectedRole]);

  // Fetch subaccounts when switching from Merchant to a role that needs subaccount
  useEffect(() => {
    if (showSubaccountDropdown && selectedRole?.merchantId) {
      const fetchSubaccounts = async () => {
        const fetched = await getSubaccountOptions(selectedRole.merchantId);
        setSubaccounts(fetched);
      };
      fetchSubaccounts();
    } else {
      setSubaccounts([]);
      setSelectedSubaccount("");
    }
  }, [showSubaccountDropdown, selectedRole?.merchantId]);

  const handleInputChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "subaccount") {
      setSelectedSubaccount(value);
    } else {
      setEditedRole(value);
      setSelectedSubaccount("");
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const selectedRoles: Role[] = userRoles.filter(roleObj => roleObj.role === selectedRole.role);
      const selectedRolesCount: number = selectedRoles.length;
      const isHandleRoleRemove = selectedRolesCount === 1 ? true : false;

      const postData: any = {
        email: email,
        role: editedRole,
        selectedRole: selectedRole?.role,
        username,
        merchantId: selectedRole?.merchantId,
        subaccountId: selectedRole?.subaccountId,
        userRole,
        sysUsername,
        isHandleRoleRemove,
      };

      // When changing from Merchant to a role that needs subaccount
      if (isOriginalMerchant && needsSubaccount(editedRole)) {
        postData.newSubaccountId = selectedSubaccount;
      }

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/${userRole}/user/action/update-user-role`,
        postData
      );

      // Close modal first, then show success message
      setIsSubmitting(false);
      onClose();

      setSwalProps({
        show: true,
        icon: "success",
        title: "Success",
        html: "Role successfully updated!",
        showConfirmButton: true,
        didClose: () => {
          setSwalProps({});
          fetchUserRoles(true);
        },
      });
    } catch (error) {
      setIsSubmitting(false);
      onClose();

      console.error("Error updating user role:", error);
      setSwalProps({
        show: true,
        icon: "error",
        title: "Oops!",
        html: "Failed to change role. Please try again.",
        showConfirmButton: true,
        didClose: () => {
          setSwalProps({});
        },
      });
    }
  };

  const isSubmitDisabled =
    selectedRole?.role === editedRole ||
    (showSubaccountDropdown && !selectedSubaccount);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
        <ModalOverlay className="bg-[#000] !opacity-30" />
        <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
          <ModalBody className="p-6 md:p-7">
            <div className="mb-4 flex items-start gap-3">
              <div className="rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-400 dark:ring-primary-900/30">
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="currentColor"
                >
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                </svg>
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Edit User Role
                </h1>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  Update the role for this user
                </p>
              </div>
            </div>

            <div className="mb-5 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/5">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Username:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{username}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Email:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{email}</span>
                </div>
                {currentMerchantName && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Merchant:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{currentMerchantName}</span>
                  </div>
                )}
                {currentSubaccountName && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Subaccount:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{currentSubaccountName}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Current Role:</span>
                  <span className="inline-flex items-center rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-semibold text-primary-700 ring-1 ring-inset ring-primary-600/20 dark:bg-primary-900/20 dark:text-primary-300 dark:ring-primary-400/30">
                    {selectedRole?.role}
                  </span>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-5">
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
                  Role
                </label>
                <RoleDropdown
                  isDisabled={isSubmitting}
                  newUser={{ role: editedRole }}
                  handleInputChange={handleInputChange}
                  roleOptions={roleOptions}
                />
              </div>

              {showSubaccountDropdown &&
                <div className="mb-5">
                  <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
                    Subaccount <span className="text-red-500">*</span>
                  </label>
                  <SubaccountDropdown
                    isDisabled={isSubmitting}
                    newUser={{ subaccount: selectedSubaccount }}
                    handleInputChange={handleInputChange}
                    merchants={merchants}
                    subaccounts={subaccounts}
                    isSetSubaccounts={isSetSubaccounts}
                  />
                </div>
              }

              <div className="flex justify-end gap-2">
                <Button
                  onClick={onClose}
                  isDisabled={isSubmitting}
                  className="dark:bg-neutral-900 dark:hover:bg-neutral-800 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-white/10 dark:text-white"
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  isDisabled={isSubmitDisabled || isSubmitting}
                  isLoading={isSubmitting}
                  loadingText="Updating..."
                  className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  Update user
                </Button>
              </div>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default EditRoleModal;
